import React, { useEffect } from "react";
import { X } from "lucide-react";

// Lightweight modal shell used by the Forge advanced popups (Stress &
// Margin, Strategy Log & Alpha Insights). Closes on backdrop click and Esc.
//
// Props:
//   open:     bool
//   title:    string
//   subtitle: optional string under the title
//   onClose:  () => void
//   children: modal body
//   maxWidth: tailwind max-w-* class (default: max-w-5xl)
const ForgeModal = ({
  open,
  title,
  subtitle,
  onClose,
  children,
  maxWidth = "max-w-5xl",
  testid = "forge-modal",
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // Lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[180] bg-[#050912]/80 backdrop-blur-md flex items-start justify-center overflow-y-auto p-4 sm:p-8"
      data-testid={testid}
      onClick={onClose}
    >
      <div
        className={`relative w-full ${maxWidth} bg-[#0B1426] border border-white/10 rounded-xl shadow-2xl my-2`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/10 bg-[#0E1A30] rounded-t-xl">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] uppercase tracking-[0.22em] text-white/85 font-medium truncate">
              {title}
            </div>
            {subtitle && (
              <div className="text-[10.5px] text-white/45 mt-0.5 truncate">{subtitle}</div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-white/10 text-white/60 hover:text-white hover:bg-white/5 transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

export default ForgeModal;
